import { FC } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ProjectTasksDataTableDocument,
  ProjectTasksDataTableQuery,
} from 'generated/resources/datatable.graphql'
import {
  BulkDuplicateTasksDocument,
  ProjectTaskAnalyticsDocument,
} from 'generated/resources/graphql'

import { useDataTable } from 'modules/core/components/DataTable'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onCloseModal: () => void
}

const DuplicateModal: FC<Props> = ({ onCloseModal }) => {
  const { selectedRows, data } = useDataTable<ProjectTasksDataTableQuery>()
  const ids = [...selectedRows].map((v) => String(v))
  const projectName = data.find((t) => t.id === ids[0])?.task?.relatedResourceTitle

  const [bulkDuplicateTasks, { loading }] = useMutation(BulkDuplicateTasksDocument, {
    extractorFn: (response) =>
      response.projectManagementMutations.bulkDuplicateProjectmanagementProjectTask,
  })

  const trackEvent = () => {
    track(trackingEvents.projectManagement.tasks.duplicateProjectTask, {
      number: ids.length,
    })
  }

  const onSubmitModal = async () => {
    trackEvent()
    const result = await bulkDuplicateTasks({
      variables: { ids },
      awaitRefetchQueries: true,
      refetchQueries: [ProjectTasksDataTableDocument, ProjectTaskAnalyticsDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
        time: 2000,
      })
    }
    onCloseModal()
  }

  return (
    <Modal
      illustration={'documents'}
      title={i18n.tp('project_management.tasks.bulk_actions.duplicate.title', {
        project_name: projectName,
        count: ids.length,
      })}
      description={i18n.tp('project_management.tasks.bulk_actions.duplicate.description', {
        count: ids.length,
      })}
      onClose={onCloseModal}
      onSubmit={onSubmitModal}
      footer={
        <>
          <Button type="secondary" onClick={onCloseModal}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} submit>
            {i18n.t('project_management.tasks.bulk_actions.duplicate.label')}
          </Button>
        </>
      }
    />
  )
}

export default DuplicateModal
